import { useState, useEffect, useCallback } from "react"
import { SleepRecord } from "@/types/sleep"
import { getChildSleepRecords } from "@/lib/services/supabase-service"
import { logger } from "@/lib/services/logger-service"
import { useAuth } from "@/hooks/use-auth"

interface UseSleepRecordsResult {
  data: SleepRecord[]
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

declare global {
  interface Window {
    refreshSleepHistory?: () => Promise<void>;
  }
}

export function useSleepRecords(childId: string, limit?: number, records?: SleepRecord[]): UseSleepRecordsResult {
  const [data, setData] = useState<SleepRecord[]>(records || [])
  const [isLoading, setIsLoading] = useState(!records)
  const [error, setError] = useState<Error | null>(null)
  const { user } = useAuth()
  
  // Create a memoized refresh function
  const refresh = useCallback(async () => {
    if (!childId) return;
    
    logger.info('Manually refreshing sleep records', { childId, timestamp: new Date().toISOString() });
    setIsLoading(true);
    
    try {
      const sleepRecords = await getChildSleepRecords(childId, limit);
      
      logger.info('Sleep records refreshed successfully', { 
        childId, 
        recordCount: sleepRecords.length,
        firstRecordDate: sleepRecords[0]?.start_time || 'No records'
      });
      
      setData(sleepRecords);
    } catch (err) {
      logger.error('Failed to refresh sleep records', { childId, error: err });
      setError(err instanceof Error ? err : new Error('Failed to refresh sleep records'));
    } finally {
      setIsLoading(false);
    }
  }, [childId, limit]);
  
  // Register the refresh function globally
  useEffect(() => {
    if (childId) {
      // Expose the refresh function globally so it can be called after insert/update
      window.refreshSleepHistory = refresh;
      
      return () => {
        // Clean up when component unmounts
        window.refreshSleepHistory = undefined;
      };
    }
  }, [refresh, childId]);
  
  useEffect(() => {
    // Debug log to show which child ID is being used
    logger.info('Fetching sleep records for child', { childId, limit, hasProvidedRecords: !!records });
    
    if (records) {
      setData(records)
      return
    }
    
    const fetchSleepRecords = async () => {
      setIsLoading(true)
      try {
        // No need to pass caregiverId anymore as we've simplified the function
        const sleepRecords = await getChildSleepRecords(childId, limit)
        
        logger.info('Sleep records fetched successfully', { 
          childId, 
          recordCount: sleepRecords.length,
          firstRecordDate: sleepRecords[0]?.start_time || 'No records'
        });
        
        setData(sleepRecords)
      } catch (err) {
        logger.error('Failed to fetch sleep records', { childId, error: err });
        setError(err instanceof Error ? err : new Error('Failed to fetch sleep records'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchSleepRecords()
  }, [childId, limit, records, user?.id])

  return { data, isLoading, error, refresh }
}
